import { GlBasis } from "@common/schemas/accounting-integration.schema";
import type { MigrationRequiredPeriod } from "../migrationRequiredPeriod";
import { assessFulfillment } from "./assessFulfillment";
import {
    CollectedExtraction,
    CoverageRow,
    Fulfillment,
    RequirementKey,
    RequirementSpec,
} from "./types";

export const QBDT_BASIS_LABELS = {
    coa: "Chart of accounts",
    cashGl: "Cash GL",
    accrualGl: "Accrual GL",
} as const;

export function requiredGlBasisKeys(hasAccrualLedger: boolean): GlBasis[] {
    return hasAccrualLedger ? ["cashGl", "accrualGl"] : ["cashGl"];
}

export function buildQbdtRequirements(
    requiredPeriod: MigrationRequiredPeriod | null,
    hasAccrualLedger: boolean,
): RequirementSpec[] {
    const requirements: RequirementSpec[] = [
        {
            kind: "singleton",
            key: "coa",
            label: QBDT_BASIS_LABELS.coa,
        },
        {
            kind: "continuous",
            key: "cashGl",
            period: requiredPeriod,
            label: QBDT_BASIS_LABELS.cashGl,
        },
    ];
    if (hasAccrualLedger) {
        requirements.push({
            kind: "continuous",
            key: "accrualGl",
            period: requiredPeriod,
            label: QBDT_BASIS_LABELS.accrualGl,
        });
    }
    return requirements;
}

function isLeadingGapPeriod(
    periodStart: string | null | undefined,
    requiredPeriod: MigrationRequiredPeriod,
): boolean {
    return (
        requiredPeriod.start !== null && periodStart === requiredPeriod.start
    );
}

export function isLeadingGapOnly(
    awaitingRow: CoverageRow,
    requiredPeriod: MigrationRequiredPeriod,
): boolean {
    return isLeadingGapPeriod(awaitingRow.period?.start, requiredPeriod);
}

export type BasisBlockingKind = "missing_basis" | "gap";

export interface BasisBlockingOutcome {
    key: RequirementKey;
    kind: BasisBlockingKind;
    period: CoverageRow["period"];
}

export function basisBlockingOutcomes(
    rows: CoverageRow[],
    requiredPeriod: MigrationRequiredPeriod | null,
    hasAccrualLedger: boolean,
): BasisBlockingOutcome[] {
    const outcomes: BasisBlockingOutcome[] = [];
    for (const key of requiredGlBasisKeys(hasAccrualLedger)) {
        const basisRows = rows.filter((row) => row.requirementKey === key);
        const hasReadyGl = basisRows.some((row) => row.status === "ready");
        if (!hasReadyGl) {
            outcomes.push({ key, kind: "missing_basis", period: null });
            continue;
        }
        if (requiredPeriod === null) {
            continue;
        }
        for (const row of basisRows) {
            if (
                row.status === "awaiting" &&
                !isLeadingGapOnly(row, requiredPeriod)
            ) {
                outcomes.push({ key, kind: "gap", period: row.period });
            }
        }
    }
    return outcomes;
}

// An expected basis with no ready GL at all is "omitted". Under the any-of
// finalize policy an omitted basis is allowed (it becomes a per-basis warning),
// unless *every* expected basis is omitted — then there is nothing to migrate and
// finalize is hard-blocked. Coverage gaps within a provided basis are NOT handled
// here; a hole between or after covered periods is always a hard blocker (see
// `basisBlockingOutcomes`), never a warning.
//
// To later require *all* bases instead: callers should treat every `omittedKey`
// as a blocker rather than a warning (and the backend should block on them too).
export interface BasisFinalizeVerdict {
    omittedKeys: RequirementKey[];
    isNoBasisProvided: boolean;
}

export function basisFinalizeVerdict(
    rows: CoverageRow[],
    hasAccrualLedger: boolean,
): BasisFinalizeVerdict {
    const expectedKeys = requiredGlBasisKeys(hasAccrualLedger);
    const omittedKeys = expectedKeys.filter(
        (key) =>
            !rows.some(
                (row) => row.requirementKey === key && row.status === "ready",
            ),
    );
    return {
        omittedKeys,
        isNoBasisProvided: omittedKeys.length === expectedKeys.length,
    };
}

export function blockingBasisKeys(
    rows: CoverageRow[],
    requiredPeriod: MigrationRequiredPeriod | null,
    hasAccrualLedger: boolean,
): RequirementKey[] {
    const keys = new Set<RequirementKey>();
    for (const outcome of basisBlockingOutcomes(
        rows,
        requiredPeriod,
        hasAccrualLedger,
    )) {
        keys.add(outcome.key);
    }
    return requiredGlBasisKeys(hasAccrualLedger).filter((key) => keys.has(key));
}

export interface QbdtMigrationFulfillment extends Fulfillment {
    blockingBasisKeys: RequirementKey[];
}

export function assessQbdtMigrationFulfillment(args: {
    requiredPeriod: MigrationRequiredPeriod | null;
    hasAccrualLedger: boolean;
    extractions: CollectedExtraction[];
}): QbdtMigrationFulfillment {
    const fulfillment = assessFulfillment({
        requirements: buildQbdtRequirements(
            args.requiredPeriod,
            args.hasAccrualLedger,
        ),
        extractions: args.extractions,
    });
    return {
        ...fulfillment,
        blockingBasisKeys: blockingBasisKeys(
            fulfillment.rows,
            args.requiredPeriod,
            args.hasAccrualLedger,
        ),
    };
}
